// src/components/Navbar.jsx
import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSocialDetails } from "../hooks/useSocialDetails";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { catLink as defaultCatLink } from "../constants/navCategories";
import { fetchAllCategoryData, buildMergedGroups } from "../utils/categoryTree";
import "./Navbar.css";

const PhoneIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>);
const UserIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>);
const PackageIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16.5 9.4l-9-5.19"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29 7 12 12 20.71 7"/><line x1="12" y1="22" x2="12" y2="12"/></svg>);
const TruckIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><circle cx="7" cy="21" r="2"/><path d="M9 21h6"/><circle cx="17" cy="21" r="2"/><path d="M17 3v5h2a2 2 0 0 1 2 2v7"/></svg>);
const SearchIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>);
const ChevronDownIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>);
const CartIcon = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>);
const HelpIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>);


const SERVICES = [
  { label: "Artwork Guidelines",      path: "/artwork-guidelines" },
  { label: "Delivery Information",    path: "/contact" },
  { label: "Trade & Reseller Pricing",path: "/contact" },
  { label: "Request a Custom Quote",  path: "/contact" },
];

// ── Simple dropdown — click to open, click sub to navigate ───────────────────
function NavGroupDropdown({ group, allProducts }) {
  const [open, setOpen] = useState(false);
  const [hoveredSub, setHoveredSub] = useState(null);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [flipCard, setFlipCard] = useState(false);
  const [hoverItemTop, setHoverItemTop] = useState(null); // viewport top of the hovered item
  const [cardTop, setCardTop] = useState(0); // card's top, relative to the mega-menu wrapper
  const btnRef = useRef(null);
  const cardRef = useRef(null);
  const timerRef = useRef(null);

  function openMenu() {
    clearTimeout(timerRef.current);
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const cardWidth = 430;
      const listWidth = 230;
      // Would the product card overflow the right edge if placed to the right of the list?
      const overflowsRight = rect.left + listWidth + cardWidth > window.innerWidth - 10;
      setFlipCard(overflowsRight); // if it overflows, the card opens to the LEFT of the list instead
      // The list itself always opens directly under the button — it never moves.
      setMenuStyle({
        position: "fixed",
        top: rect.bottom + 2,
        left: rect.left,
        zIndex: 99999,
      });
    }
    setOpen(true);
  }

  function closeMenu() {
    timerRef.current = setTimeout(() => {
      setOpen(false);
      setHoveredSub(null);
      setHoveredProduct(null);
      setHoverItemTop(null);
    }, 150);
  }

  function keepOpen() { clearTimeout(timerRef.current); }

  // Products for hovered sub-category (already resolved by buildMergedGroups,
  // whether this is a default SinaLite-matched sub or a custom admin one)
  const subProducts = hoveredSub?.products
    ? (hoveredSub.isCustom ? hoveredSub.products : hoveredSub.products.slice(0, 8))
    : [];

  const rightVisible = !!hoveredSub;


  useLayoutEffect(() => {
    if (!rightVisible || hoverItemTop == null) return;
    const wrapperTop = menuStyle.top || 0;
    const cardHeight = cardRef.current ? cardRef.current.offsetHeight : 0;
    let top = hoverItemTop - wrapperTop; // align card's top with the hovered item's top
    const maxTop = window.innerHeight - 10 - wrapperTop - cardHeight;
    if (top > maxTop) top = maxTop;
    if (top < 0) top = 0;
    setCardTop(top);
  }, [rightVisible, hoverItemTop, menuStyle.top, subProducts.length]);

  const cardTitle = hoveredSub?.label;
  const cardDesc  = hoveredProduct ? hoveredProduct.description : hoveredSub?.desc;
  const cardImage = hoveredProduct?.imageUrl || hoveredSub?.imageUrl;

  return (
    <div
      ref={btnRef}
      onMouseEnter={openMenu}
      onMouseLeave={closeMenu}
      style={{ position: "relative" }}
    >
      <button type="button" className="nav-group-btn">
        {group.label} <ChevronDownIcon />
      </button>

      {open && (
        <div
          className="mega-menu"
          style={menuStyle}
          onMouseEnter={keepOpen}
          onMouseLeave={closeMenu}
        >
          {/* Sub-category list — always sized to its own content, always stays put */}
          <div className={"mega-menu-list" + (rightVisible ? (flipCard ? " mega-menu-list-card-left" : " mega-menu-list-card-right") : "")}>
            {group.subs.map(sub => {
              const key = sub.cat;
              const isActive = hoveredSub && hoveredSub.cat === key;
              return (
                <Link
                  key={key}
                  to={defaultCatLink(sub.cat)}
                  className={"mega-menu-item" + (isActive ? " active" : "")}
                  onMouseEnter={(e) => {
                    setHoveredSub(sub);
                    setHoveredProduct(null);
                    setHoverItemTop(e.currentTarget.getBoundingClientRect().top);
                  }}
                  onClick={() => setOpen(false)}
                >
                  {sub.label}
                  <span className="mega-menu-arrow">{flipCard ? "‹" : "›"}</span>
                </Link>
              );
            })}
          </div>

          {/* Card: floats to the right normally, or to the left when it would overflow the screen */}
          {rightVisible && (
            <div
              ref={cardRef}
              className={"mega-menu-card" + (flipCard ? " mega-menu-card-left" : " mega-menu-card-right")}
              style={{ top: cardTop }}
            >
              {/* Top section: title+desc LEFT, category image RIGHT — fixed, never changes */}
              <div className="mega-menu-card-top">
                <div className="mega-menu-card-info">
                  <h4 className="mega-menu-card-title">{cardTitle}</h4>
                  <div className="mega-menu-card-desc-wrap">
                    {cardDesc && <p className="mega-menu-card-desc">{cardDesc}</p>}
                  </div>
                </div>
                {cardImage && (
                  <div className="mega-menu-card-img">
                    <img src={cardImage} alt={hoveredProduct?.name || hoveredSub?.label} />
                  </div>
                )}
              </div>

              {/* Divider */}
              {subProducts.length > 0 && <div className="mega-menu-divider" />}

              {/* Products list below divider */}
              {subProducts.length > 0 && (
                <div
                  className="mega-menu-card-products"
                  onMouseLeave={() => setHoveredProduct(null)}
                >
                  <span className="mega-menu-card-label">Products</span>
                  {subProducts.map(p => (
                    <Link
                      key={p.id}
                      to={p.isCustom ? `/products/custom/${p.id}` : `/products/${p.id}`}
                      className={"mega-menu-card-product-link" + (hoveredProduct?.id === p.id ? " active" : "")}
                      onMouseEnter={() => setHoveredProduct(p)}
                      onClick={() => setOpen(false)}
                    >
                      {p.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [navProducts, setNavProducts] = useState([]);
  const [navMeta, setNavMeta] = useState({});
  const [overrides, setOverrides] = useState({});
  const [productOverrides, setProductOverrides] = useState({});
  const [row2Top, setRow2Top] = useState(90); // fallback until measured
  const row1Ref = useRef(null);

  useLayoutEffect(() => {
    function measure() {
      if (row1Ref.current) setRow2Top(row1Ref.current.offsetHeight);
    }
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    import("../services/sinaliteService").then(({ fetchProducts }) => {
      fetchProducts()
        .then(({ allProducts, categories }) => {
          setNavProducts(allProducts);
          // Build meta map: catName -> { imageUrl, description }
          const meta = {};
          categories.forEach(c => { meta[c.name] = { imageUrl: c.imageUrl, desc: c.description }; });
          setNavMeta(meta);
        })
        .catch(() => {});
    });
    fetchAllCategoryData()
      .then(({ overrides, productOverrides }) => { setOverrides(overrides); setProductOverrides(productOverrides); })
      .catch(() => {});
  }, []);

  const mergedGroups = useMemo(() => buildMergedGroups({
    allProducts: navProducts,
    overrides,
    productOverrides,
  }), [navProducts, overrides, productOverrides]);

  const enrichedGroups = useMemo(() => mergedGroups.map(group => ({
    ...group,
    subs: group.subs.map(sub => ({
      ...sub,
      imageUrl: (sub.cat && navMeta[sub.cat]?.imageUrl) || sub.imageUrl || "",
      desc:     (sub.cat && navMeta[sub.cat]?.desc)     || sub.desc     || "",
    })),
  })), [mergedGroups, navMeta]);
  const { data: sd, loading: socialLoading } = useSocialDetails();
  const { totalItems } = useCart();
  const { user, profile, logout } = useAuth();
  const phone = socialLoading ? "" : (sd.phone || "0800 123 4567");
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const showUserMenu = user && !isAdminRoute;

  useEffect(() => setMobileOpen(false), [location]);

  function handleSearch(e) {
    e.preventDefault();
    if (searchTerm.trim()) navigate("/products?q=" + encodeURIComponent(searchTerm.trim()));
  }

  return (
    <>
      {/* ROW 1 */}
      <div className="nav-row1" ref={row1Ref}>
        <div className="container nav-row1-inner">
          <Link to="/" className="nav-logo">
            <div className="logo-icon">
              <img src="/logo.jpeg" alt="Circuit Printing Solutions" className="logo-img" />
            </div>
          </Link>

          <form className="nav-search" onSubmit={handleSearch}>
            <input type="text" placeholder="Search (e.g. business cards, banners, etc.)" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            <button type="submit" aria-label="Search"><SearchIcon /></button>
          </form>

          <div className="nav-row1-actions">
            <div className="nav-dropdown-wrap">
              <button className="nav-toplink">Services <ChevronDownIcon /></button>
              <div className="nav-services-dropdown">
                {SERVICES.map(s => <Link key={s.label} to={s.path}>{s.label}</Link>)}
              </div>
            </div>

            <Link to="/blogs" className="nav-toplink">Blogs</Link>
            <Link to="/faq" className="nav-toplink"><HelpIcon /> Help</Link>
            {phone && <a href={"tel:" + phone.replace(/\s/g, "")} className="nav-toplink nav-phone"><PhoneIcon /> {phone}</a>}
            <div className="nav-divider" />

            {showUserMenu ? (
              <div className="nav-user-menu">
                <button className="nav-user-btn">
                  <UserIcon />
                  <span>{(profile?.fullName || "Account").split(" ")[0]}</span>
                  <ChevronDownIcon />
                </button>
                <div className="nav-user-dropdown">
                  <div className="nav-user-dropdown-inner">
                    <div className="nav-user-info">
                      <strong>{profile?.fullName || "My Account"}</strong>
                      <span>{user.email}</span>
                    </div>
                    <Link to="/account">My Orders</Link>
                    <Link to="/account">My Profile</Link>
                    <button onClick={logout} className="nav-user-logout">Sign Out</button>
                  </div>
                </div>
              </div>
            ) : (
              <Link to="/login" className="nav-toplink"><UserIcon /> Account</Link>
            )}

            <Link to="/account" className="nav-toplink nav-orders-link"><PackageIcon /> My Orders</Link>
            <Link to="/cart" className="nav-cart-btn" aria-label="Cart">
              <CartIcon />
              {totalItems > 0 && <span className="nav-cart-count">{totalItems}</span>}
              <span className="nav-cart-label">Cart</span>
            </Link>
          </div>

          <button className="hamburger" onClick={() => setMobileOpen(!mobileOpen)}>
            <span></span><span></span><span></span>
          </button>
        </div>
      </div>

      {/* ROW 2 */}
      <nav className="nav-row2" style={{ top: row2Top }}>
        <div className="container nav-row2-inner">
          <Link to="/" className="nav-row2-link">Home</Link>
          {enrichedGroups.map(group => (
            <NavGroupDropdown key={group.label} group={group} allProducts={navProducts} />
          ))}
          <Link to="/about" className="nav-row2-link">About</Link>
          <Link to="/contact" className="nav-row2-link">Contact</Link>
          <Link to="/submit-order" className="nav-row2-cta">Custom Order</Link>
        </div>
      </nav>

      {/* Delivery Strip */}
      <div className="nav-delivery-strip">
        <div className="container nav-delivery-inner">
          <TruckIcon />
          <span className="nav-delivery-text">
            <strong>For shipping options</strong>
            {phone && (
              <>
                &nbsp;— Call us:&nbsp;
                <a href={"tel:" + phone.replace(/\s/g, "")} style={{ color: "var(--cyan)", fontWeight: 700, textDecoration: "none" }}>{phone}</a>
              </>
            )}
          </span>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="mobile-menu">
          <Link to="/">Home</Link>
          <Link to="/products">All Products</Link>
          {mergedGroups.map(group => (
            <div key={group.slug}>
              <Link to={group.groupLink} style={{ fontWeight: 700, display: "block", padding: "8px 20px", color: "#fff" }}>
                {group.label}
              </Link>
              {group.subs.map(sub => (
                <Link
                  key={sub.cat}
                  to={defaultCatLink(sub.cat)}
                  style={{ paddingLeft: 36, fontSize: 13, display: "block", padding: "5px 20px 5px 36px", color: "rgba(255,255,255,0.6)" }}
                >
                  {sub.label}
                </Link>
              ))}
            </div>
          ))}
          <Link to="/blogs">Blogs</Link>
          <Link to="/about">About</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/faq">Help / FAQ</Link>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", margin: "8px 0", padding: "12px 20px 0" }}>
            {showUserMenu ? (
              <>
                <Link to="/account" style={{ display: "block", color: "rgba(255,255,255,0.7)", fontSize: 14, marginBottom: 8 }}>My Account</Link>
                <button onClick={logout} style={{ background: "none", border: "none", color: "#f87171", fontSize: 14, padding: 0, cursor: "pointer" }}>Sign Out</button>
              </>
            ) : (
              <Link to="/login" style={{ display: "block", color: "rgba(255,255,255,0.7)", fontSize: 14, marginBottom: 8 }}>Sign In</Link>
            )}
            {phone && <a href={"tel:" + phone.replace(/\s/g, "")} style={{ display: "block", color: "rgba(255,255,255,0.6)", fontSize: 13, marginTop: 10 }}>📞 {phone}</a>}
          </div>
          <Link to="/contact" className="btn-primary" style={{ marginTop: 12, textAlign: "center" }}>Get a Quote</Link>
        </div>
      )}
    </>
  );
}